import axios from "axios";
import Constants from "./constants";
import CryptoJS from "crypto-js";
import moment from "./moment";

class Utils {
  static encryptBase64 = (id: string) => {
    let t = CryptoJS.enc.Utf8.parse(id);
    let tt = CryptoJS.enc.Base64.stringify(t);
    return tt;
  };
  static decryptBase64 = (encoded: string) => {
    let t = CryptoJS.enc.Base64.parse(encoded);
    let tt = CryptoJS.enc.Utf8.stringify(t);
    return tt;
  };
  static encrypt = (id: string) => {
    let t = CryptoJS.AES.encrypt(
      id,
      process.env.REACT_APP_ENCRYPTION_PK || ""
    ).toString();
    let tt = encodeURIComponent(t);
    return tt;
  };
  static decrypt = (encoded: string) => {
    let bytes = CryptoJS.AES.decrypt(
      encoded,
      process.env.REACT_APP_ENCRYPTION_PK || ""
    );
    let t = bytes.toString(CryptoJS.enc.Utf8);
    let tt = decodeURIComponent(t);
    return tt;
  };
  static capitalizeFirstLetter = (str: string): string =>
    str?.length > 0 ? str?.charAt(0).toUpperCase() + str?.slice(1) : "";

  static titleCase = (str: string): string => {
    const splitStr = str.toLowerCase().split(" ");
    for (let i = 0; i < splitStr.length; i++) {
      splitStr[i] =
        splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    return splitStr.join(" ");
  };

  static getIp = async () => {
    const ipResponse = await axios.get(`https://api.ipify.org/?format=json`);
    const ip = (ipResponse && ipResponse.data && ipResponse.data.ip) || "";
    return ip;
  };

  static groupArrayOfObjects = (list: any[], key: string) =>
    list.reduce((rv, x) => {
      (rv[x[key]] = rv[x[key]] || []).push(x);
      return rv;
    }, {});

  static formatToNumber = (amount: string, percision: number = 2) =>
    parseFloat(amount)
      .toFixed(percision)
      .replace(/\d(?=(\d{3})+\.)/g, "$&,")
      .replace(".00", "")
      .replace(".0", "");

  static formatToNumber2 = (amount: string, percision: number = 2) => {
    return parseInt(amount)
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, "$&,")
      .replace(".00", "");
  };

  static EMPTY_VALUE = "—";

  static formatToCurrency = (
    amount: any,
    percision: number = 2,
    currency = "$"
  ) => {
    if (amount === undefined || amount === null || amount === "") {
      return Utils.EMPTY_VALUE;
    }
    const num = parseFloat(amount);
    if (Number.isNaN(num)) {
      return Utils.EMPTY_VALUE;
    }
    let val = num
      .toFixed(percision)
      .replace(/\d(?=(\d{3})+\.)/g, "$&,")
      .replace(".00", "")
      .replace(".0", "");

    return currency + val;
  };

  /**
   * Renders a percentage change between two numbers (e.g. opening price → sold
   * price). Returns the em-dash placeholder when either input is missing or
   * the base price is zero, so we never flash NaN% during loading.
   */
  static formatPercentChange = (from: any, to: any, percision: number = 0) => {
    const fromNum = parseFloat(from);
    const toNum = parseFloat(to);
    if (
      from === undefined ||
      from === null ||
      to === undefined ||
      to === null ||
      Number.isNaN(fromNum) ||
      Number.isNaN(toNum) ||
      fromNum === 0
    ) {
      return Utils.EMPTY_VALUE;
    }
    const pct = ((toNum - fromNum) / fromNum) * 100;
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct.toFixed(percision)}%`;
  };

  static formatToCurrency2 = (
    n: number,
    percision: number = 1,
    currency = "$"
  ) => {
    if (n < 1e3) return currency + n.toFixed(percision).replace(".0", "");
    if (n >= 1e3 && n < 1e6)
      return currency + (n / 1e3).toFixed(percision).replace(".0", "") + "K";
    if (n >= 1e6 && n < 1e9)
      return currency + (n / 1e6).toFixed(percision).replace(".0", "") + "M";
    if (n >= 1e9 && n < 1e12)
      return currency + (n / 1e9).toFixed(percision).replace(".0", "") + "B";
    if (n >= 1e12)
      return currency + (n / 1e12).toFixed(percision).replace(".0", "") + "T";
  };

  static getRandomColor = () => {
    const r = Math.round(Math.random() * 255);
    const g = Math.round(Math.random() * 255);
    const b = Math.round(Math.random() * 255);
    return {
      background: `rgba(${r},${g},${b},.7)`,
      border: `rgba(${r},${g},${b},1)`,
    };
  };

  static getCalculatedValue = (val: number) => {
    let _v: number = 0;
    if (val <= 500) _v = val;
    else if (val > 500 && val < 1200) _v = val / 2;
    else if (val > 1200 && val < 2500) _v = val / 3;
    else if (val > 2500 && val < 3200) _v = val / 4;
    else if (val > 3200) _v = Constants.THUMBNAIL_SIZE;
    return Math.floor(_v);
  };

  static getFileExtension = (name: string) => name?.split(".").pop();
  static getFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Byte";
    var k = 1000,
      dm = 2,
      sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
      i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  static replaceAll = (str: string, find: string, replace: string) =>
    str.replace(new RegExp(find, "g"), replace);

  static dateTimeFormatForPicker = "MM-dd-yyyy @ HH:mm";
  static dateFormatForPicker = "MM-dd-yyyy";
  static dateFormatForLabel = "MM-DD-yyyy";
  static dateTimeFormatForLabel = "MM-DD-yyyy @ hh:mm A z";

  static formatDateTime = (date: any) => {
    return moment(date).format(this.dateTimeFormatForLabel);
  };
  static formatDateTimeInTz = (date: any, timezone?: string) => {
    if (timezone) {
      return moment(date).tz(timezone).format(this.dateTimeFormatForLabel);
    }
    return moment(date).format(this.dateTimeFormatForLabel);
  };
  static formatDate = (date: any) => {
    return moment(date).format(this.dateFormatForLabel);
  };
  static formatFromNow = (date: any) => {
    return moment(date).fromNow();
  };

  // Countdown for the complaint/claim window. Expressed in days while more
  // than a day remains (the window is 7 days), then in hours as it winds down
  // — so the buyer/seller see "in 6 days" early on and "in 23 hours" near the
  // end, never moment's vague "in a day".
  static formatHoursFromNow = (date: any) => {
    const minutes = moment(date).diff(moment(), "minutes");
    if (minutes <= 0) return "soon";
    if (minutes < 60) return "in less than an hour";
    const hours = Math.round(minutes / 60);
    if (hours < 24) return hours === 1 ? "in 1 hour" : `in ${hours} hours`;
    const days = Math.round(hours / 24);
    return days === 1 ? "in 1 day" : `in ${days} days`;
  };

  // Helper for the "Bid ends on" field hint on Create/Update pages.
  // The close-a-bid cron runs daily at 00:00 UTC and closes any auction
  // whose biddingEnds has passed — so the actual close moment is the
  // next 00:00 UTC at-or-after biddingEnds. This formats that exact
  // moment in the seller's chosen timezone, falling back to the static
  // "Auctions close once daily at 00:00 UTC." sentence when either input
  // is missing.
  static formatBidEndsHint = (biddingEnds: any, timezone?: string): string => {
    if (!biddingEnds) return "Auctions close at the top of every hour UTC.";
    const m = moment.utc(biddingEnds);
    if (!m.isValid())
      return "Auctions close at the top of every hour UTC.";
    // The cron runs hourly and closes any ad whose biddingEnds has
    // already passed. So a biddingEnds picked exactly on the hour closes
    // at that hour; anything between hour marks closes at the next one.
    const hourStart = m.clone().startOf("hour");
    const closeUtc = m.isSame(hourStart) ? hourStart : hourStart.add(1, "hour");
    const local = timezone ? closeUtc.clone().tz(timezone) : closeUtc.clone();
    const tzAbbr = timezone ? local.zoneAbbr() : "UTC";
    return `Auction will close on ${local.format("D MMM YYYY")} at ${local.format(
      "h:mm A"
    )} ${tzAbbr}.`;
  };

  // Single source of truth for whether a user's profile has every field
  // we require before they can act as a seller. Mirrors the rule in
  // pages/dashboard/Profile.tsx so badge state and gating stay in sync.
  // Address fields are duplicated in Profile.tsx's stricter "complete"
  // check; we extend it slightly to also require the buyer-side
  // address1/2/city/state/zip because shipping won't work without them.
  static isProfileComplete = (user: any): boolean => {
    if (!user) return false;
    return Boolean(
      user.name &&
        user.email &&
        user.phone &&
        user.dob &&
        user.address1 &&
        user.address2 &&
        user.city &&
        user.state &&
        user.zip &&
        user.speciality
    );
  };

  // Returns the gate state for "can this user create an artwork?".
  // Order: verification → profile → Trustap full account. Each blocker
  // is reported one at a time so the user fixes the most fundamental
  // one first. `hasFullTrustapAccount` is optional and defaults to true
  // so existing call sites that don't pass it keep their prior behavior;
  // pages that gate on Trustap should pass it explicitly.
  static canCreateListing = (
    user: any,
    isVerifiedUser: boolean,
    hasFullTrustapAccount: boolean = true
  ):
    | { ok: true }
    | {
        ok: false;
        reason: "not-verified" | "incomplete-profile" | "no-full-trustap";
      } => {
    if (!isVerifiedUser) return { ok: false, reason: "not-verified" };
    if (!Utils.isProfileComplete(user))
      return { ok: false, reason: "incomplete-profile" };
    if (!hasFullTrustapAccount)
      return { ok: false, reason: "no-full-trustap" };
    return { ok: true };
  };

  static checkPasswordStrength = (value: string) => {
    const v = value || "";
    const length = v.length >= 8;
    const letter = /[A-Za-z]/.test(v);
    const number = /\d/.test(v);
    const special = /[^A-Za-z0-9]/.test(v);
    return { length, letter, number, special, ok: length && letter && number && special };
  };
}

export default Utils;
